import { rem } from '@mantine/core';
import { noteSide } from '@reditelji/shared';
import { useEffect, useState } from 'react';
import { useNoteMutations, useNotes } from '../api/notes';
import { useSession } from '../api/session';
import type { VolunteerRow } from '../api/volunteers';
import { recordedAt } from './cancelCopy';
import { CARD, INPUT, MONO } from './ui';
import { useCoordinatorLabel } from '../lib/people';

const SIDE_LABEL = { organizacija: 'organizacija', redarji: 'redarji' } as const;

/**
 * Organizacija — the thread between the race organisation and the marshal team
 * about one volunteer (SPEC §1.1L). The volunteer never sees it and nothing in
 * it is ever sent; notes are not edited or deleted.
 *
 * An organizer opening the tab marks the team's replies read. The team keeps no
 * such marker: for them a thread is settled by answering, not by looking.
 */
export function OrganizationTab({ volunteer: v }: { volunteer: VolunteerRow }) {
  const { me, can } = useSession();
  const labelOf = useCoordinatorLabel();
  const notes = useNotes(v.id);
  const mut = useNoteMutations(v.id);
  const [draft, setDraft] = useState('');
  const mySide = me ? noteSide(me.role) : 'redarji';
  const canWrite = can('notes:write');

  const rows = notes.data ?? [];
  // Read up to the newest team reply this tab has actually drawn — never "now",
  // which would also cover a reply written after the list loaded. Keyed on that
  // note and the roster flag, not on the mutation object, which is new on
  // every render: once per reply that arrives while the tab is open.
  const unread = v.notesUnreadForMe;
  const seenTeamNoteId = rows.findLast((n) => noteSide(n.authorRole) === 'redarji')?.id;
  const { markRead } = mut;
  useEffect(() => {
    if (mySide === 'organizacija' && unread && seenTeamNoteId) markRead.mutate(seenTeamNoteId);
  }, [mySide, unread, seenTeamNoteId]);

  const send = () => {
    const body = draft.trim();
    // Ctrl+Enter reaches here without the button's disabled state; a note is never deleted.
    if (!body || mut.add.isPending) return;
    mut.add.mutate(body, { onSuccess: () => setDraft('') });
  };

  return (
    <div style={{ ...CARD, padding: '12px 16px 16px', maxWidth: 900 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '6px 10px',
          marginBottom: 14,
          borderRadius: 7,
          background: 'oklch(0.97 0.02 25)',
          color: 'oklch(0.45 0.12 25)',
          fontSize: rem(12),
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        MED EKIPAMA — {v.name.toUpperCase()} TEGA NE VIDI
      </div>

      {notes.isPending ? (
        <div style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>Nalagam opombe…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>
          {mySide === 'organizacija'
            ? 'Še ni opomb. Vprašaj ekipo redarjev o tem prostovoljcu.'
            : 'Še ni opomb. Tu organizacija dirke sprašuje o tem prostovoljcu, redarji odgovarjajo.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((n) => {
            const side = noteSide(n.authorRole);
            const mine = side === mySide;
            return (
              <div key={n.id} style={{ maxWidth: '78%', alignSelf: mine ? 'flex-end' : 'flex-start' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'baseline',
                    justifyContent: mine ? 'flex-end' : 'flex-start',
                    marginBottom: 3,
                  }}
                >
                  <span
                    style={{
                      fontSize: rem(12.5),
                      fontWeight: 600,
                      color: side === 'organizacija' ? 'oklch(0.5 0.12 25)' : 'oklch(0.4 0.012 90)',
                    }}
                  >
                    {labelOf(n.authorId, n.authorName)} · {SIDE_LABEL[side]}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: rem(11), color: 'oklch(0.6 0.012 90)' }}>
                    {recordedAt(n.createdAt)}
                  </span>
                </div>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: rem(13.5),
                    lineHeight: 1.5,
                    padding: '9px 12px',
                    borderRadius: 9,
                    border: '1px solid oklch(0.92 0.006 95)',
                    borderLeft: `3px solid ${side === 'organizacija' ? 'oklch(0.6 0.14 25)' : 'oklch(0.85 0.008 95)'}`,
                    background: mine ? 'oklch(0.975 0.004 95)' : 'oklch(1 0 0)',
                    color: 'oklch(0.28 0.012 90)',
                  }}
                >
                  {n.body}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canWrite ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid oklch(0.94 0.005 95)',
          }}
        >
          <textarea
            value={draft}
            maxLength={2000}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
            }}
            placeholder={
              mySide === 'organizacija' ? 'Vprašaj ekipo redarjev …' : 'Odgovori organizaciji …'
            }
            style={{
              ...INPUT,
              flex: 1,
              minHeight: 60,
              resize: 'vertical',
              lineHeight: 1.5,
              boxSizing: 'border-box',
            }}
          />
          <button
            onClick={send}
            disabled={!draft.trim() || mut.add.isPending}
            style={{
              flexShrink: 0,
              cursor: draft.trim() ? 'pointer' : 'default',
              padding: '8px 16px',
              borderRadius: 7,
              border: 0,
              background: draft.trim() ? 'oklch(0.3 0.012 90)' : 'oklch(0.9 0.006 95)',
              color: draft.trim() ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
              fontFamily: 'inherit',
              fontSize: rem(13),
              fontWeight: 600,
            }}
          >
            Pošlji
          </button>
        </div>
      ) : null}
    </div>
  );
}
